import React, { useState } from 'react';
import { VerisureItem, VerisureRepair, ResaleValuation } from '../../types';
import { calculateResaleValuation } from '../../services/resaleValuationService';
import { toast } from 'react-hot-toast';

export interface ResaleValuationCardProps {
  item: VerisureItem;
  repairs?: VerisureRepair[];
  className?: string;
}

export const ResaleValuationCard: React.FC<ResaleValuationCardProps> = ({
  item,
  repairs = [],
  className = '',
}) => {
  const [valuation] = useState<ResaleValuation>(() =>
    calculateResaleValuation(item, repairs)
  );

  const formatPrice = (val: number) => {
    return `${valuation.currency === 'USD' ? '$' : '₦'}${val.toLocaleString()}`;
  };

  const handleDownloadCertificate = () => {
    // Generate a printable certificate receipt
    const certWindow = window.open('', '_blank');
    if (!certWindow) {
      toast.error('Pop-up blocked. Please allow popups to view certificate.');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>VERISURE Valuation Certificate - ${item.assetId}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f8fafc; color: #0f172a; padding: 40px; margin: 0; }
          .cert-box { max-width: 650px; margin: 0 auto; background: #ffffff; border: 3px solid #ff5a00; border-radius: 24px; padding: 36px; box-shadow: 0 10px 30px rgba(0,0,0,0.08); position: relative; }
          .header { text-align: center; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; }
          .logo { height: 60px; }
          .title { font-size: 24px; font-weight: 900; color: #17191d; margin: 12px 0 4px; }
          .subtitle { font-size: 13px; color: #ff5a00; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
          .val-hero { text-align: center; margin: 28px 0; background: linear-gradient(135deg, #fff7ed, #ffedd5); border: 1px solid #fed7aa; border-radius: 16px; padding: 20px; }
          .val-label { font-size: 12px; font-weight: 800; color: #c2410c; text-transform: uppercase; letter-spacing: 1px; }
          .val-amount { font-size: 40px; font-weight: 900; color: #9a3412; margin: 6px 0; }
          .val-bonus { font-size: 13px; font-weight: 700; color: #15803d; }
          .grid { display: grid; grid-cols: 2; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; font-size: 13px; }
          .item-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
          .label { color: #64748b; font-weight: 600; }
          .value { font-weight: 800; color: #0f172a; }
          .footer { text-align: center; font-size: 11px; color: #94a3b8; margin-top: 24px; border-top: 1px solid #f1f5f9; padding-top: 16px; }
        </style>
      </head>
      <body>
        <div class="cert-box">
          <div class="header">
            <img src="/official.png" class="logo" alt="Verisure" />
            <div class="title">Verified Resale Valuation Certificate</div>
            <div class="subtitle">Secure • Assured • Always</div>
          </div>
          <div class="val-hero">
            <div class="val-label">Certified Fair Market Valuation</div>
            <div class="val-amount">${formatPrice(valuation.currentEstimatedValue)}</div>
            <div class="val-bonus">Includes +${formatPrice(valuation.verisureValueBonus)} Verisure Verified History Premium</div>
          </div>
          <div class="grid">
            <div>
              <div class="item-row"><span class="label">Equipment:</span><span class="value">${item.make} ${item.model}</span></div>
              <div class="item-row"><span class="label">Asset ID:</span><span class="value">${item.assetId}</span></div>
              <div class="item-row"><span class="label">Condition Score:</span><span class="value">${item.conditionScore}/100</span></div>
            </div>
            <div>
              <div class="item-row"><span class="label">Repair Cycles:</span><span class="value">${valuation.repairCyclesCount} Verified Logs</span></div>
              <div class="item-row"><span class="label">Value Retention:</span><span class="value">${valuation.valueRetentionPercentage}% of MSRP</span></div>
              <div class="item-row"><span class="label">Trust Grade:</span><span class="value">${valuation.confidenceGrade} (Prime Tier)</span></div>
            </div>
          </div>
          <div class="footer">
            Tamper-Evident Ledger Backed by Verisure Insurance Protocol • Generated ${new Date().toLocaleDateString()}
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `;

    certWindow.document.write(html);
    certWindow.document.close();
    toast.success('Resale Certificate opened!');
  };

  return (
    <div className={`bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl relative overflow-hidden ${className}`}>
      {/* Background Subtle Gradient Glow */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-gradient-to-r from-red-500/10 via-orange-500/15 to-amber-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Dynamic Valuation Engine
            </span>
            <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">
              Grade {valuation.confidenceGrade}
            </span>
          </div>
          <h3 className="text-xl font-black text-white">Estimated Resale Valuation</h3>
        </div>

        <button
          type="button"
          onClick={handleDownloadCertificate}
          className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-orange-500/40 text-orange-400 text-xs font-bold px-3.5 py-2 rounded-xl transition flex items-center gap-1.5 shadow-sm"
          title="Print official valuation report for buyer verification"
        >
          Valuation Certificate
        </button>
      </div>

      {/* Hero Value Banner */}
      <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-5 mb-6 relative">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              Estimated Fair Market Value
            </div>
            <div className="text-3xl sm:text-4xl font-black text-white tracking-tight flex items-baseline gap-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-400 to-amber-400">
                {formatPrice(valuation.currentEstimatedValue)}
              </span>
              <span className="text-xs font-normal text-slate-400">
                (Orig. MSRP ~ {formatPrice(valuation.originalMSRP)})
              </span>
            </div>
          </div>

          <div className="bg-emerald-950/60 border border-emerald-800/60 p-3 rounded-xl flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div>
              <div className="text-[11px] text-emerald-400 font-extrabold uppercase tracking-wide">
                Verisure Value Premium
              </div>
              <div className="text-sm font-black text-emerald-300">
                +{formatPrice(valuation.verisureValueBonus)} (+{Math.round((valuation.verisureValueBonus / (valuation.currentEstimatedValue - valuation.verisureValueBonus || 1)) * 100)}%)
              </div>
              <div className="text-[10px] text-emerald-400/80">vs unverified market peers</div>
            </div>
          </div>
        </div>

        {/* Value Retention Progress Bar */}
        <div className="mt-5 pt-4 border-t border-slate-800/80">
          <div className="flex justify-between text-xs font-bold mb-2">
            <span className="text-slate-300">Value Retention Rate</span>
            <span className="text-orange-400 font-mono">{valuation.valueRetentionPercentage}% of Original MSRP</span>
          </div>
          <div className="w-full bg-slate-800 h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-400 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, valuation.valueRetentionPercentage)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Cycle & Maintenance Metric Drivers */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-xl">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
            Repair Cycles
          </div>
          <div className="text-lg font-black text-white">
            {valuation.repairCyclesCount}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">Immutable Logs</div>
        </div>

        <div className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-xl">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
            Condition Score
          </div>
          <div className="text-lg font-black text-emerald-400">
            {item.conditionScore}/100
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">Live Certified Metric</div>
        </div>

        <div className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-xl">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
            OEM Authenticity
          </div>
          <div className="text-lg font-black text-orange-400">
            +{valuation.partsAuthenticityBonus}%
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">Parts Value Bonus</div>
        </div>

        <div className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-xl">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
            Peer Unverified
          </div>
          <div className="text-lg font-black text-slate-400">
            {formatPrice(valuation.marketComparisonUnverified)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Paperless Baseline</div>
        </div>
      </div>

      {/* Summary recommendation */}
      <div className="bg-slate-950/60 border border-slate-800/80 rounded-xl p-3 text-xs text-slate-300 flex items-start gap-2.5">
        <div className="w-5 h-5 rounded-md bg-orange-500/10 text-orange-400 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <p className="leading-relaxed">{valuation.summaryRecommendation}</p>
      </div>
    </div>
  );
};
